import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../Store';
import { colors, fonts, glyphs, useMediaQuery } from '../../../Styles';
import { NewInputList, ToggleBox} from '../..';
import { ListStyle_Types, NewFieldStyles, NewInputListHandles } from '../../../Definitions';
import { Graph_QL, useSystem, useUser } from "../../../Services"
import { User_Data } from '../../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/userCalls';

// TODO
import Setting from '../../Settings/setting';
import { Farm_Data } from '../../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/farmCalls';
import { NewButtonList } from '../../BaseComponents/newButton';
import { createPortal } from 'react-dom';
import { ToggleStyles } from '../../BaseComponents/toggleBox';

const farmFields = ["name", "city", "street", "phone", "vat", "email"] as const;
const userFields = ["firstname", "lastname", "phone", "email", "address", "country", "username"] as const;

export default function UserAndFarmSettings({onDirtyChange, zIndex}:{onDirtyChange: (dirty: boolean) => void, zIndex?: number}) {
  const {t} = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 570px)`);
  const responsiveBox = useMediaQuery(`(max-width: 1420px)`);

  // #region | GraphQL
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({ domain: farm })), [farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm })), [farm]);
  const { data: systemsState, call: getSystemCall } = useSystem(connection);
  const { userToken, getUserData, userData, updateUserData, userEmails, getFarmData, farmData: farmDataArray, updateFarmData, getFarmSettings, setCurrentUserAttentionEmailSystems, getUserEmailsInfo } = useUser() 
  
  useEffect(() => {
    getSystemCall({ fetchAmount: 50 })
  
    if (userToken && farm) {
      getUserData(userToken)
      getUserEmailsInfo(domain)
      getFarmData(farm)
      getFarmSettings(farm)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [farm, userToken]);
  // #endregion
    
  // #region | User Management
  const systems = useMemo(() => {
    return systemsState?.[domain]?.data || [];
  }, [domain, systemsState]);
    
  const [userSystems, setUserSystems] = useState(systems.map(e => ({ ...e, isChecked: userEmails?.[0]?.system_ids?.includes(e.system_id) ?? false })));
  const initialUserSystemsRef = useRef<number[]>([]);

  useEffect(() => {
    if (!systems.length) return;
  
    const mapped = systems.map(e => ({ ...e, isChecked: userEmails?.[0]?.system_ids?.includes(e.system_id) ?? false }));
  
    setUserSystems(mapped);
    initialUserSystemsRef.current = mapped
      .filter(s => s.isChecked)
      .map(s => s.system_id);
  }, [systems, userEmails]);



const handleUserSystemsUpdate = () => {
  setUserSystems(prev => {
    const systemsToAdd = prev.filter(e => e.isChecked).map(e => e.system_id);
    console.log("Saving systems:", systemsToAdd);
    setCurrentUserAttentionEmailSystems(domain, systemsToAdd).catch(() => {});
    return prev;
  });
};
  // #endregion

  // #region | User Data
  const [userForm, seUserForm] = useState<Omit<User_Data, "user_id">>({firstname: "", lastname: "", phone: "", email: "", address: "", country: "", username: ""});
  const userInputRef = useRef<NewInputListHandles>(null)
  
  const loadUserData = useCallback(() => {
    if (userData !== undefined) {
      userInputRef.current?.refs.current[0].setValue(userData.firstname)
      userInputRef.current?.refs.current[1].setValue(userData.lastname)
      userInputRef.current?.refs.current[2].setValue(userData.phone)
      userInputRef.current?.refs.current[3].setValue(userData.email)
      userInputRef.current?.refs.current[4].setValue(userData.address)
      userInputRef.current?.refs.current[5].setValue(userData.country)
      userInputRef.current?.refs.current[6].setValue(userData.username)
    }
  }, [userData])

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleUserDataUpdate = () => {
    var NewUserData = {
      firstname: userInputRef.current?.refs.current[0].value ?? "",
      lastname: userInputRef.current?.refs.current[1].value ?? "",
      phone: userInputRef.current?.refs.current[2].value ?? "",
      email: userInputRef.current?.refs.current[3].value ?? "",
      address: userInputRef.current?.refs.current[4].value ?? "",
      country: userInputRef.current?.refs.current[5].value ?? ""
    } as User_Data
    updateUserData(NewUserData.firstname, NewUserData.lastname, NewUserData.phone, NewUserData.email, NewUserData.address, NewUserData.country)
  }
  // #endregion

  // #region | Farm Data
  const [farmForm, seFarmForm] = useState<Omit<Farm_Data, "company_id" | "domain">>({name: "", city: "", street: "", phone: "", vat: "", email: ""});
  const farmInputRef=useRef<NewInputListHandles>(null)
  
  const loadFarmData = useCallback(() => {
    if (farmDataArray !== undefined) {
      const farmData = farmDataArray[0];
      farmInputRef.current?.refs.current[0].setValue(farmData?.name);
      farmInputRef.current?.refs.current[1].setValue(farmData?.city);
      farmInputRef.current?.refs.current[2].setValue(farmData?.street);
      farmInputRef.current?.refs.current[3].setValue(farmData?.phone);
      farmInputRef.current?.refs.current[4].setValue(farmData?.vat);
      farmInputRef.current?.refs.current[5].setValue(farmData?.email);
    }
  }, [farmDataArray]);
  
  useEffect(() => {
    loadFarmData();
  }, [loadFarmData]);

  const handleFarmDataUpdate = () => {
    var NewFarmData = {
      name: farmInputRef.current?.refs.current[0].value ?? "",
      city: farmInputRef.current?.refs.current[1].value ?? "",
      street: farmInputRef.current?.refs.current[2].value ?? "",
      phone: farmInputRef.current?.refs.current[3].value ?? "",
      vat: farmInputRef.current?.refs.current[4].value ?? "",
      email: farmInputRef.current?.refs.current[5].value ?? ""
    } as Farm_Data
    updateFarmData( NewFarmData.name, NewFarmData.city, NewFarmData.street, NewFarmData.phone, NewFarmData.vat, NewFarmData.email, farm)
  }
  // #endregion

  useEffect(() => {
    if (userData) seUserForm(userData);
    if (farmDataArray) seFarmForm(farmDataArray[0]);
  }, [farmDataArray, userData]);

  // Handle on changes
  const onChanges = userFields.map((field) => {
    return (value: string) => {
      seUserForm(prev => ({ ...prev, [field]: value }));
    };
  }); farmFields.map((field) => {
    return (value: string) => {
      seFarmForm(prev => ({ ...prev, [field]: value }));
    };
  })
  
  // Dirty Data
  const dirtyData = useMemo(() => {
    const userDirty = userData
      ? userFields.some(field => userForm[field] !== userData[field])
      : false;
  
    const farmData = farmDataArray?.[0];
    const farmDirty = farmData
      ? farmFields.some(field => farmForm[field] !== farmData[field])
      : false;
  
    // 👇 Systems dirty check
    const currentSystems = userSystems.filter(s => s.isChecked).map(s => s.system_id).sort();
    const initialSystems = [...initialUserSystemsRef.current].sort();
  
    const systemsDirty =
      currentSystems.length !== initialSystems.length ||
      currentSystems.some((id, i) => id !== initialSystems[i]);
  
    return userDirty || farmDirty || systemsDirty;
  }, [userForm, userData, farmForm, farmDataArray, userSystems]);
  

  useEffect(() => {
    onDirtyChange?.(dirtyData);
  }, [dirtyData, onDirtyChange]);
  
  return ( <>
    {/* {dirtyData ? <Overlay beforeUnload onClickAlert /> : <></>} */}
    <div style={{ position: "relative", zIndex: zIndex ? zIndex : 0, display: "flex", gap: "20px", alignItems: mobileXs ? "center" : "end", justifyContent: "center", flexDirection: "column", width: !responsiveBox ? "1050px" : "100%" }}>

      {/* User Settings */}
      <Setting startEnhancer={glyphs.User({size: "25px"})} header={t("user.userInformation", {defaultValue: "user information"})} content={
        <NewInputList ref={userInputRef}
          placeholders={["", "", "", "", "", "", ""]}
          headers={[
            t("user.firstName", {defaultValue: "First Name"}), 
            t("user.lastName", {defaultValue: "Last Name"}),
            t("user.phone", {defaultValue: "Phone"}),
            t("user.email", {defaultValue: "Email"}), 
            t("user.country", {defaultValue: "Country"}),
            t("user.address", {defaultValue: "Address"}),
            t("user.userName", {defaultValue: "User Name"})
          ]}
          isRequired={Array.from({ length: 7 }, () => false)}
          isDisabled={Array.from({ length: 7 }, (_, index) => index === 6)}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyTertiary
          }}
          onChanges={onChanges}
          listOverrides={{
            listStyle: mobileXs ? ListStyle_Types.centeredGroup : ListStyle_Types.group
          }}
        />
      }/>

      {/* Farm Settings */}
      <Setting startEnhancer={glyphs.FarmInfo({size: "25px"})} header={t("farm.farmInformation", {defaultValue: "farm information"})} content={
        <NewInputList ref={farmInputRef} 
          placeholders={["", "", "", "", "", ""]}
          headers={[
            t("farm.farmName", {defaultValue: "farm name"}), 
            t("farm.city", {defaultValue: "city"}),
            t("farm.street", {defaultValue: "street"}),
            t("farm.phone", {defaultValue: "phone"}),
            t("farm.vat", {defaultValue: "vat"}),
            t("farm.email", {defaultValue: "email"})
          ]}
          // isRequired={Array.from({ length: 6 }, () => true)} 
          isDisabled={Array.from({ length: 6 }, (_, index) => index === 0)}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyTertiary
          }}
          onChanges={onChanges}
          listOverrides={{
            listStyle: mobileXs ? ListStyle_Types.centeredGroup : ListStyle_Types.group
          }}
        />
      }/>

      <Setting
        header={t("globalSettingsPages.globalSettings.userManagement", {defaultValue: "user management"})}
        startEnhancer={glyphs.User({ size: "25px" })} 
        content={
          <div style={{ display: "flex", flexDirection: "column", gap: "5px"}}>     
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>
              {t("globalSettingsPages.globalSettings.emailAttentionDisclaimerUser", { defaultValue: "Choose which systems you want to receive attentions from" })}:
            </p>
              
            <div style={{display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 50px", width: "fit-content"}}>
              {userSystems.map(system =>
                <ToggleBox
                  text={system.name}
                  isChecked={system.isChecked}
                  onChange={() =>
                    setUserSystems(prev =>
                      prev.map(s =>
                        s.system_id === system.system_id
                          ? { ...s, isChecked: !s.isChecked }
                          : s
                      )
                    )
                  }
                  key={system.system_id}
                  icon={glyphs.Mail({ size: "16px" })}
                  toggleStyles={ToggleStyles.greyTertiary}
                />
              )}  
            </div>
          </div>
        }
      />

    </div>

    {/* Save/discard btns */}
    {dirtyData && createPortal(
      <div style={{ position: "absolute", zIndex: dirtyData ? "999999" : "0", bottom: "40px", left: "50%", transform: "translateX(-50%)", backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d", }}>
        <NewButtonList 
          contents={[t("globalSettingsPages.globalSettings.saveChanges", {defaultValue: "save changes"}), t("globalSettingsPages.globalSettings.discardChanges", {defaultValue: "discard changes"})]} 
          onClick={(i => {
            if (i === 0) {
              if ( !dirtyData ) {
                return;
              }
              handleUserDataUpdate();
              handleFarmDataUpdate();
              handleUserSystemsUpdate();

              initialUserSystemsRef.current = userSystems
              .filter(s => s.isChecked)
              .map(s => s.system_id);
            }
            else if (i === 1) {
              loadUserData();
              loadFarmData();
              
              if (userData) seUserForm(userData);
              if (farmDataArray?.[0]) seFarmForm(farmDataArray[0]);
              // setUserSystems( systems.map(e => ({ ...e, isChecked: initialUserSystemsRef.current.includes(e.system_id) })));
            }
          })}
          overrides={(i) => ({
            baseStyles: { backgroundColor: i === 0 ? colors.blueDarkPrimary : colors.greyDarkPrimary, border: i === 1 ? "3px solid "+colors.blueDarkPrimary : "none"},
            hoverStyles: { backgroundColor: i === 0 ? colors.blueDarkSecondary : colors.blueDarkPrimary },
          })}
          listOverrides={{
            listStyle: mobileXs ? ListStyle_Types.vertical : ListStyle_Types.horizontal
          }}    
        />
      </div>, document.body)
    }
  </> );
}